<script lang="ts">
  import {
    type Language,
    LANGUAGE_LABELS,
    LANGUAGES,
    type Translate,
  } from "../i18n";
  import Icon from "./Icon.svelte";

  const {
    soundEnabled,
    volume,
    statsVisible,
    fpsVisible,
    symbolsVisible,
    language,
    t,
    onToggleSound,
    onVolume,
    onToggleStats,
    onToggleFps,
    onToggleSymbols,
    onSelectLanguage,
    onClose,
  }: {
    soundEnabled: boolean;
    volume: number;
    statsVisible: boolean;
    fpsVisible: boolean;
    symbolsVisible: boolean;
    language: Language;
    t: Translate;
    onToggleSound: () => void;
    onVolume: (volume: number) => void;
    onToggleStats: () => void;
    onToggleFps: () => void;
    onToggleSymbols: () => void;
    onSelectLanguage: (language: Language) => void;
    onClose: () => void;
  } = $props();

  /** The switch rows, in the order they read. */
  const switches = $derived([
    {
      key: "sound",
      label: t("settings.sound"),
      on: soundEnabled,
      toggle: onToggleSound,
    },
    {
      key: "scoreboard",
      label: t("settings.scoreboard"),
      on: statsVisible,
      toggle: onToggleStats,
    },
    {
      key: "fps",
      label: t("settings.fps"),
      on: fpsVisible,
      toggle: onToggleFps,
    },
    {
      key: "shapes",
      label: t("settings.shapes"),
      on: symbolsVisible,
      toggle: onToggleSymbols,
    },
  ]);
</script>

<section class="overlay" aria-label={t("settings.title")}>
  <div class="dialog dialog--settings">
    <h2>{t("settings.title")}</h2>

    <div class="settings">
      {#each switches as row (row.key)}
        <div class="settings__row">
          <span class="settings__label">
            {#if row.key === "shapes"}
              <span class="settings__glyph"
                ><Icon name="shapes" size={16} /></span
              >
            {/if}
            {row.label}
          </span>
          <button
            type="button"
            class="settings__switch"
            role="switch"
            aria-checked={row.on}
            aria-label={row.label}
            onclick={row.toggle}
          >
            {row.on ? t("settings.on") : t("settings.off")}
          </button>
        </div>
      {/each}

      <div class="settings__row">
        <span class="settings__label" id="settings-volume">
          {t("settings.volume")}
        </span>
        <div class="slider">
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={volume}
            disabled={!soundEnabled}
            style="--volume-fill:{Math.round(volume * 100)}%"
            aria-labelledby="settings-volume"
            oninput={(event) => onVolume(Number(event.currentTarget.value))}
          />
        </div>
      </div>

      <div class="settings__row">
        <span class="settings__label">{t("settings.language")}</span>
        <div
          class="settings__choices"
          role="group"
          aria-label={t("settings.language")}
        >
          {#each LANGUAGES as option (option)}
            <button
              type="button"
              class="settings__choice"
              aria-pressed={option === language}
              onclick={() => onSelectLanguage(option)}
            >
              {LANGUAGE_LABELS[option]}
            </button>
          {/each}
        </div>
      </div>
    </div>

    <button type="button" onclick={onClose}>{t("settings.close")}</button>
  </div>
</section>
